/*
 * WarbleInput.tsx
 * Input wrapper with animated warble effect using SVG filters.
 * Applies a turbulence-based displacement effect that can be triggered programmatically.
 */

'use client';

import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import gsap from 'gsap';

// Styles
import styles from './WarbleInput.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

// -------------------- Default Configuration
// Self-contained defaults for portability to other projects

const DEFAULT_CONFIG = {
  MAX_STRENGTH: 40,
  DURATION: 1200,
  BASE_FREQ_X: 0.018,
  BASE_FREQ_Y: 0.004,
  FREQ_VARIATION_X: 0.005,
  FREQ_VARIATION_Y: 0.002,
  FREQ_TIME_MULTIPLIER_X: 1.2,
  FREQ_TIME_MULTIPLIER_Y: 0.8,
  NOISE_INTENSITY: 0.003,
  FREQ_CHANGE_STEPS: 8,
  OCTAVES: 2,
} as const;

// -------------------- Types

type WarbleConfig = {
  [K in keyof typeof DEFAULT_CONFIG]: number;
};

interface WarbleInputProps {
  children: React.ReactNode;
  borderRadius?: string;
  className?: string;
  config?: WarbleConfig; // Warble configuration object
}

export interface WarbleInputHandle {
  triggerWarble: () => void;
}

// -------------------- Component

const WarbleInput = forwardRef<WarbleInputHandle, WarbleInputProps>(
  ({ children, borderRadius = '1rem', className = '', config = DEFAULT_CONFIG }, ref) => {
    const turbRef = useRef<SVGFETurbulenceElement>(null);
    const dispRef = useRef<SVGFEDisplacementMapElement>(null);
    const [isWarbling, setIsWarbling] = React.useState(false);

    const triggerWarble = () => {
      setIsWarbling(true);

      const animData = {
        scale: config.MAX_STRENGTH,
        time: 0,
      };

      gsap.to(animData, {
        scale: 0,
        time: config.DURATION / 1000,
        duration: config.DURATION / 1000,
        ease: 'steps(' + config.FREQ_CHANGE_STEPS + ')',
        onUpdate: () => {
          const turb = turbRef.current;
          const disp = dispRef.current;
          if (!turb || !disp) return;

          // Add random noise for jagged, unpredictable movement
          const noiseX = (Math.random() - 0.5) * config.NOISE_INTENSITY;
          const noiseY = (Math.random() - 0.5) * config.NOISE_INTENSITY;

          // Vary frequency with sine wave + random noise
          const freqX = config.BASE_FREQ_X + Math.sin(animData.time * config.FREQ_TIME_MULTIPLIER_X) * config.FREQ_VARIATION_X + noiseX;
          const freqY = config.BASE_FREQ_Y + Math.sin(animData.time * config.FREQ_TIME_MULTIPLIER_Y) * config.FREQ_VARIATION_Y + noiseY;
          turb.setAttribute('baseFrequency', `${freqX} ${freqY}`);

          disp.setAttribute('scale', String(animData.scale));
        },
        onComplete: () => {
          setIsWarbling(false);
        },
      });
    };

    useImperativeHandle(ref, () => ({
      triggerWarble,
    }));

    return (
      <div className={cx('warble-input-wrap', className, { 'is-warbling': isWarbling })} style={{ borderRadius }}>
        {children}
        <div className={cx('warble-border')} style={{ borderRadius }} />

        <svg className={cx('warble-svg')}>
          <filter id="warbleEffect">
            <feTurbulence
              ref={turbRef}
              type="fractalNoise"
              baseFrequency={`${config.BASE_FREQ_X} ${config.BASE_FREQ_Y}`}
              numOctaves={String(config.OCTAVES)}
              result="noise"
              seed="0"
            />
            <feDisplacementMap
              ref={dispRef}
              in="SourceGraphic"
              in2="noise"
              scale="0"
              xChannelSelector="G"
              yChannelSelector="R"
            />
          </filter>
        </svg>
      </div>
    );
  }
);

WarbleInput.displayName = 'WarbleInput';

export default WarbleInput;
